// Landing-page copy only. Keep all three translations together when changing copy.
// Buyer names, source URLs and report images remain in Radar.tsx.
export type RadarLocale = 'en' | 'zh-Hans' | 'zh-Hant';
export const RADAR_LOCALES = [
  { locale: 'en', label: 'English', shortLabel: 'EN', path: '/buyer-radar/', ogLocale: 'en_US' },
  { locale: 'zh-Hans', label: '简体中文', shortLabel: '简体', path: '/buyer-radar/zh-hans/', ogLocale: 'zh_CN' },
  { locale: 'zh-Hant', label: '繁體中文', shortLabel: '繁體', path: '/buyer-radar/zh-hant/', ogLocale: 'zh_HK' },
] as const;

export function radarLocaleFromPath(path: string): RadarLocale | null {
  return RADAR_LOCALES.find((item) => item.path.replace(/\/$/, '') === path.replace(/\/$/, ''))?.locale ?? null;
}

const messages = {
  languageLabel: {
    en: "Page language",
    "zh-Hans": "页面语言",
    "zh-Hant": "頁面語言",
  },
  brandHome: {
    en: "Mindstream Buyer Radar home",
    "zh-Hans": "Mindstream 买家雷达首页",
    "zh-Hant": "Mindstream 買家雷達首頁",
  },
  navLabel: {
    en: "Radar page navigation",
    "zh-Hans": "雷达页面导航",
    "zh-Hant": "雷達頁面導覽",
  },
  navSignals: {
    en: "Signals",
    "zh-Hans": "商机信号",
    "zh-Hant": "商機訊號",
  },
  navSample: {
    en: "Sample report",
    "zh-Hans": "示例报告",
    "zh-Hant": "示例報告",
  },
  navPilot: {
    en: "Free trial",
    "zh-Hans": "免费试用",
    "zh-Hant": "免費試用",
  },
  apply: {
    en: "Start free trial",
    "zh-Hans": "开始免费试用",
    "zh-Hant": "開始免費試用",
  },
  heroKicker: {
    en: "Buyer signal radar · Hong Kong exporters",
    "zh-Hans": "买家商机雷达 · 面向香港出口商",
    "zh-Hant": "買家商機雷達 · 面向香港出口商",
  },
  heroTitle: {
    en: "Know which overseas buyers are entering a buying window.",
    "zh-Hans": "看清哪些海外买家正进入采购窗口。",
    "zh-Hant": "看清哪些海外買家正進入採購窗口。",
  },
  heroDescription: {
    en: "Find companies that fit your market, review the evidence, and prepare your next conversation in one private workspace.",
    "zh-Hans": "在专属工作台寻找目标市场中的匹配公司、查看来源证据，并准备下一次沟通。",
    "zh-Hant": "在專屬工作台尋找目標市場中的匹配公司、查看來源證據，並準備下一次溝通。",
  },
  applyFounding: {
    en: "Start free trial",
    "zh-Hans": "开始免费试用",
    "zh-Hant": "開始免費試用",
  },
  viewReport: {
    en: "View a real report",
    "zh-Hans": "查看真实报告",
    "zh-Hant": "查看真實報告",
  },
  proofLabel: {
    en: "Free trial and current validation",
    "zh-Hans": "免费试用及当前进展",
    "zh-Hant": "免費試用及目前進展",
  },
  slots: {
    en: "Open to every verified email",
    "zh-Hans": "验证邮箱即可注册",
    "zh-Hant": "驗證電郵即可註冊",
  },
  freeMonth: {
    en: "14-day free trial",
    "zh-Hans": "14 天免费试用",
    "zh-Hant": "14 天免費試用",
  },
  noInstall: {
    en: "No software install",
    "zh-Hans": "无需安装软件",
    "zh-Hant": "無需安裝軟件",
  },
  activePilots: {
    en: "{count} active pilots",
    "zh-Hans": "已有 {count} 家商家试用",
    "zh-Hant": "已有 {count} 家商家試用",
  },
  realOutput: {
    en: "Real pilot output",
    "zh-Hans": "真实试用报告",
    "zh-Hant": "真實試用報告",
  },
  sourceLinked: {
    en: "Source-linked",
    "zh-Hans": "附原始来源",
    "zh-Hant": "附原始來源",
  },
  browserLabel: {
    en: "mindstream · buyer-moment",
    "zh-Hans": "mindstream · 商机信号",
    "zh-Hant": "mindstream · 商機訊號",
  },
  heroImageAlt: {
    en: "A real Buyer-Moment card showing a buyer trigger, readiness, contact and opening draft",
    "zh-Hans": "真实商机信号卡片，展示触发事件、采购准备度、联系人和开场草稿",
    "zh-Hant": "真實商機訊號卡片，展示觸發事件、採購準備度、聯絡人和開場草稿",
  },
  windowEstimate: {
    en: "Window estimate",
    "zh-Hans": "时机判断",
    "zh-Hant": "時機判斷",
  },
  openNow: {
    en: "Open now",
    "zh-Hans": "当前窗口已开启",
    "zh-Hant": "目前窗口已開啟",
  },
  visibleEvidence: {
    en: "Evidence stays visible",
    "zh-Hans": "证据随时可查",
    "zh-Hant": "證據隨時可查",
  },
  examplesKicker: {
    en: "Five examples · open to other export categories",
    "zh-Hans": "五个示例 · 也欢迎其他出口品类",
    "zh-Hant": "五個示例 · 也歡迎其他出口品類",
  },
  examplesTitle: {
    en: "See how a buyer signal could work in your category.",
    "zh-Hans": "看看你的品类能发现哪些买家信号。",
    "zh-Hant": "看看你的品類能發現哪些買家訊號。",
  },
  examplesDescription: {
    en: "These are recent examples, not a fixed category list. Tell us what you sell; we’ll look for public events that may open a supplier window in your market.",
    "zh-Hans": "以下是近期示例，支持的品类不限于此。告诉我们你卖什么，我们会寻找目标市场中可能带来供应商合作机会的公开事件。",
    "zh-Hant": "以下是近期示例，支援的品類不限於此。告訴我們你賣什麼，我們會尋找目標市場中可能帶來供應商合作機會的公開事件。",
  },
  examplesLabel: {
    en: "Recent buyer signal examples",
    "zh-Hans": "近期买家信号示例",
    "zh-Hant": "近期買家訊號示例",
  },
  categoryBuyer: {
    en: "Category / buyer",
    "zh-Hans": "品类 / 买家",
    "zh-Hant": "品類 / 買家",
  },
  whatHappened: {
    en: "What happened",
    "zh-Hans": "发生了什么",
    "zh-Hant": "發生了什麼",
  },
  whyMatter: {
    en: "Why it may matter",
    "zh-Hans": "为什么值得关注",
    "zh-Hant": "為什麼值得關注",
  },
  evidence: {
    en: "Evidence",
    "zh-Hans": "来源证据",
    "zh-Hant": "來源證據",
  },
  analysis: {
    en: "Mindstream analysis",
    "zh-Hans": "Mindstream 分析",
    "zh-Hant": "Mindstream 分析",
  },
  checked: {
    en: "Signals checked 28 Aug 2026 · Public facts and Mindstream analysis are labelled separately.",
    "zh-Hans": "信号核查于 2026 年 8 月 28 日 · 公开事实与 Mindstream 分析分别标注。",
    "zh-Hant": "訊號核查於 2026 年 8 月 28 日 · 公開事實與 Mindstream 分析分別標示。",
  },
  openReportLabel: {
    en: "Open the real pilot report",
    "zh-Hans": "打开真实试用报告",
    "zh-Hant": "開啟真實試用報告",
  },
  feedPreviewAlt: {
    en: "An anonymized real weekly Buyer-Moment Feed",
    "zh-Hans": "已匿名处理的真实每周商机信号简报",
    "zh-Hant": "已匿名處理的真實每週商機訊號簡報",
  },
  openReport: {
    en: "Open report",
    "zh-Hans": "打开报告",
    "zh-Hant": "開啟報告",
  },
  sampleKicker: {
    en: "A real pilot report",
    "zh-Hans": "真实试用报告示例",
    "zh-Hant": "真實試用報告示例",
  },
  sampleTitle: {
    en: "Not a mockup. See the evidence and the next action.",
    "zh-Hans": "从真实报告，看证据与下一步行动。",
    "zh-Hant": "從真實報告，看證據與下一步行動。",
  },
  sampleDescription: {
    en: "Every Buyer-Moment answers four questions before a seller spends attention.",
    "zh-Hans": "每条商机信号，先帮你回答四个问题，再决定是否投入精力。",
    "zh-Hant": "每條商機訊號，先幫你回答四個問題，再決定是否投入精力。",
  },
  questionWhat: {
    en: "What happened?",
    "zh-Hans": "发生了什么？",
    "zh-Hant": "發生了什麼？",
  },
  questionWhy: {
    en: "Why now?",
    "zh-Hans": "为什么是现在？",
    "zh-Hant": "為什麼是現在？",
  },
  questionWho: {
    en: "Who should I approach?",
    "zh-Hans": "应该联系谁？",
    "zh-Hant": "應該聯絡誰？",
  },
  questionHow: {
    en: "How should I open?",
    "zh-Hans": "该怎样开场？",
    "zh-Hant": "該怎樣開場？",
  },
  moment: {
    en: "Buyer-Moment",
    "zh-Hans": "商机信号",
    "zh-Hant": "商機訊號",
  },
  formula: {
    en: "buyer + sourced trigger + window estimate + verified contact + entry angle",
    "zh-Hans": "买家 + 有来源的触发事件 + 时机判断 + 已核验联系人 + 切入角度",
    "zh-Hant": "買家 + 有來源的觸發事件 + 時機判斷 + 已核驗聯絡人 + 切入角度",
  },
  peopleMoves: {
    en: "People moves",
    "zh-Hans": "人事变动",
    "zh-Hant": "人事變動",
  },
  marketEvents: {
    en: "Market events",
    "zh-Hans": "市场事件",
    "zh-Hant": "市場事件",
  },
  importRecords: {
    en: "Import records",
    "zh-Hans": "进口记录",
    "zh-Hant": "進口紀錄",
  },
  demandChannels: {
    en: "Demand channels",
    "zh-Hans": "需求渠道",
    "zh-Hant": "需求渠道",
  },
  importLimit: {
    en: "Import records currently cover US ocean freight only.",
    "zh-Hans": "进口记录目前仅覆盖美国海运。",
    "zh-Hant": "進口紀錄目前僅涵蓋美國海運。",
  },
  offerKicker: {
    en: "Your free trial",
    "zh-Hans": "你的免费试用",
    "zh-Hant": "你的免費試用",
  },
  offerTitle: {
    en: "Find buyers and prepare the next conversation.",
    "zh-Hans": "找到买家，准备下一次沟通。",
    "zh-Hant": "找到買家，準備下一次溝通。",
  },
  whatYouGet: {
    en: "What you get",
    "zh-Hans": "你会获得什么",
    "zh-Hant": "你會獲得什麼",
  },
  weeklyEmail: {
    en: "10 company discovery credits",
    "zh-Hans": "10 个公司发现额度",
    "zh-Hant": "10 個公司發現額度",
  },
  categorySignals: {
    en: "10 contact email credits",
    "zh-Hans": "10 个联系邮箱额度",
    "zh-Hant": "10 個聯絡電郵額度",
  },
  sourceContext: {
    en: "Original source links and timing context",
    "zh-Hans": "原始来源链接与时机背景",
    "zh-Hant": "原始來源連結與時機背景",
  },
  contactsRequest: {
    en: "10 outreach draft credits",
    "zh-Hans": "10 个联络草稿额度",
    "zh-Hant": "10 個聯絡草稿額度",
  },
  reviewCall: {
    en: "Monitor up to 5 companies",
    "zh-Hans": "最多监控 5 家公司",
    "zh-Hant": "最多監控 5 家公司",
  },
  inControl: {
    en: "You stay in control",
    "zh-Hans": "决定权始终在你手中",
    "zh-Hant": "決定權始終在你手中",
  },
  noCommission: {
    en: "No commission and no role in your deal",
    "zh-Hans": "不收佣金，不参与交易",
    "zh-Hant": "不收佣金，不參與交易",
  },
  noSending: {
    en: "No auto-sending or buyer contact on your behalf",
    "zh-Hans": "不自动发信，不代你联系买家",
    "zh-Hant": "不自動發信，不代你聯絡買家",
  },
  auditable: {
    en: "Public evidence remains linked and auditable",
    "zh-Hans": "公开证据保留链接，可随时核查",
    "zh-Hant": "公開證據保留連結，可隨時核查",
  },
  youDecide: {
    en: "You decide what to verify, pursue or ignore",
    "zh-Hans": "由你决定核实、跟进或忽略哪些线索",
    "zh-Hant": "由你決定核實、跟進或忽略哪些線索",
  },
  startSmall: {
    en: "Start small",
    "zh-Hans": "先小规模试用",
    "zh-Hant": "先小規模試用",
  },
  shareCategory: {
    en: "Share your category and target markets",
    "zh-Hans": "告诉我们你的品类和目标市场",
    "zh-Hant": "告訴我們你的品類和目標市場",
  },
  optionalBuyer: {
    en: "Optionally give us one buyer to watch",
    "zh-Hans": "也可以提供一家想持续关注的买家",
    "zh-Hant": "也可以提供一家想持續關注的買家",
  },
  paidTerms: {
    en: "No payment details needed to begin",
    "zh-Hans": "开始时无需提供付款信息",
    "zh-Hant": "開始時無需提供付款資料",
  },
  noAutoCharge: {
    en: "Nothing renews or charges automatically",
    "zh-Hans": "不会自动续订或扣费",
    "zh-Hant": "不會自動續訂或扣費",
  },
  bestFor: {
    en: "Best for owner-led exporters and manufacturers already selling overseas — especially teams with certifications, trade-fair experience or a defined target-buyer list.",
    "zh-Hans": "适合由企业主直接带领、已有海外销售的出口商和制造商，尤其是具备认证、参展经验或明确目标买家名单的团队。",
    "zh-Hant": "適合由企業主直接帶領、已有海外銷售的出口商和製造商，尤其是具備認證、參展經驗或明確目標買家名單的團隊。",
  },
  coBuildSlots: {
    en: "Start with your email",
    "zh-Hans": "从邮箱开始",
    "zh-Hant": "從電郵開始",
  },
  applicationTitle: {
    en: "Tell us what you sell and where you sell it.",
    "zh-Hans": "告诉我们，你卖什么，想卖到哪里。",
    "zh-Hant": "告訴我們，你賣什麼，想賣到哪裡。",
  },
  applicationDescription: {
    en: "Verify your email to receive free credits and open your private workspace. No invitation or sales call needed.",
    "zh-Hans": "验证邮箱即可领取免费额度并进入专属工作台，无需邀请或预约沟通。",
    "zh-Hant": "驗證電郵即可領取免費額度並進入專屬工作台，無需邀請或預約溝通。",
  },
  startApplication: {
    en: "Create your free workspace",
    "zh-Hans": "创建免费工作台",
    "zh-Hant": "建立免費工作台",
  },
  consultationPrompt: {
    en: "Have a question?",
    "zh-Hans": "有疑问？",
    "zh-Hant": "有疑問？",
  },
  consultationLink: {
    en: "Ask us on WhatsApp",
    "zh-Hans": "通过 WhatsApp 问问我们",
    "zh-Hant": "透過 WhatsApp 問問我們",
  },
  signupSupport: {
    en: "Verify your email. Free credits are granted once; no payment details required.",
    "zh-Hans": "验证邮箱即可开始。免费额度只发放一次，无需提供付款信息。",
    "zh-Hant": "驗證電郵即可開始。免費額度只發放一次，無需提供付款資料。",
  },
  builtBy: {
    en: "Built by Mindstream in Hong Kong",
    "zh-Hans": "由香港 Mindstream 团队打造",
    "zh-Hant": "由香港 Mindstream 團隊打造",
  },
  coBuilding: {
    en: "Co-building with {count} active pilots",
    "zh-Hans": "正与 {count} 家试用商家共同打磨",
    "zh-Hant": "正與 {count} 家試用商家共同打磨",
  },
  privacy: {
    en: "Privacy",
    "zh-Hans": "隐私政策",
    "zh-Hant": "私隱政策",
  },
  modalTitle: {
    en: "Inside a Buyer-Moment report",
    "zh-Hans": "看看商机信号报告里有什么",
    "zh-Hant": "看看商機訊號報告裡有什麼",
  },
  closeReport: {
    en: "Close sample report",
    "zh-Hans": "关闭示例报告",
    "zh-Hant": "關閉示例報告",
  },
  reportViews: {
    en: "Sample report views",
    "zh-Hans": "示例报告视图",
    "zh-Hant": "示例報告檢視",
  },
  originalExample: {
    en: "Original report screenshot",
    "zh-Hans": "原版报告截图",
    "zh-Hant": "原版報告截圖",
  },
  weeklyFeed: {
    en: "Weekly feed",
    "zh-Hans": "每周简报",
    "zh-Hant": "每週簡報",
  },
  feedAlt: {
    en: "An anonymized weekly Mindstream Buyer-Moment Feed prepared for a pilot exporter",
    "zh-Hans": "为试用出口商制作、已匿名处理的 Mindstream 每周商机信号简报",
    "zh-Hant": "為試用出口商製作、已匿名處理的 Mindstream 每週商機訊號簡報",
  },
  feedCaption: {
    en: "A weekly, category-specific view of buyers currently entering a sourcing window.",
    "zh-Hans": "每周聚焦指定品类，呈现正进入采购窗口的买家。",
    "zh-Hant": "每週聚焦指定品類，呈現正進入採購窗口的買家。",
  },
  cardAlt: {
    en: "A real Fressnapf Buyer-Moment card with readiness, evidence, contact and an opening draft",
    "zh-Hans": "真实 Fressnapf 商机信号卡片，包含采购准备度、证据、联系人与开场草稿",
    "zh-Hant": "真實 Fressnapf 商機訊號卡片，包含採購準備度、證據、聯絡人與開場草稿",
  },
  cardCaption: {
    en: "Each signal keeps the evidence, window estimate, contact and opening angle together.",
    "zh-Hans": "每条信号将证据、时机判断、联系人和开场角度放在一起。",
    "zh-Hant": "每條訊號將證據、時機判斷、聯絡人和開場角度放在一起。",
  },
  watchlist: {
    en: "Watchlist",
    "zh-Hans": "关注列表",
    "zh-Hant": "關注列表",
  },
  watchlistAlt: {
    en: "An anonymized pet-category buyer watchlist monitored by Mindstream",
    "zh-Hans": "Mindstream 持续监测的宠物品类买家关注列表，已匿名处理",
    "zh-Hant": "Mindstream 持續監測的寵物品類買家關注列表，已匿名處理",
  },
  watchlistCaption: {
    en: "The weekly brief comes from a finite watchlist, not a generic database of millions.",
    "zh-Hans": "每周简报来自一份明确的关注列表，而非泛泛的海量企业数据库。",
    "zh-Hant": "每週簡報來自一份明確的關注列表，而非泛泛的海量企業資料庫。",
  },
  pet: {
    en: "Pet",
    "zh-Hans": "宠物用品",
    "zh-Hant": "寵物用品",
  },
  peopleMove: {
    en: "People move",
    "zh-Hans": "人事变动",
    "zh-Hant": "人事變動",
  },
  fressnapfHappened: {
    en: "Opened an Asia sourcing office in Hong Kong, led by Christopher Smith.",
    "zh-Hans": "在香港设立亚洲采购办事处，由 Christopher Smith 领导。",
    "zh-Hant": "在香港設立亞洲採購辦事處，由 Christopher Smith 領導。",
  },
  fressnapfMatters: {
    en: "A new regional buying mandate may trigger supplier-list review.",
    "zh-Hans": "新的区域采购职责，可能带来供应商名单的重新评估。",
    "zh-Hant": "新的區域採購職責，可能帶來供應商名單的重新評估。",
  },
  fressnapfSource: {
    en: "Fressnapf announcement",
    "zh-Hans": "Fressnapf 公告",
    "zh-Hant": "Fressnapf 公告",
  },
  fressnapfDate: {
    en: "11 Nov 2025",
    "zh-Hans": "2025 年 11 月 11 日",
    "zh-Hant": "2025 年 11 月 11 日",
  },
  housewares: {
    en: "Housewares",
    "zh-Hans": "家居用品",
    "zh-Hant": "家居用品",
  },
  retailExpansion: {
    en: "Retail expansion",
    "zh-Hans": "零售扩张",
    "zh-Hant": "零售擴張",
  },
  carawayHappened: {
    en: "Launched cookware and kitchen essentials in more than 500 Walmart stores.",
    "zh-Hans": "在超过 500 家 Walmart 门店推出锅具及厨房用品。",
    "zh-Hant": "在超過 500 家 Walmart 門店推出鍋具及廚房用品。",
  },
  carawayMatters: {
    en: "A DTC brand entering mass retail may need certified capacity and replenishment scale.",
    "zh-Hans": "直接面向消费者的品牌进入大众零售，可能需要具备认证的产能和规模化补货能力。",
    "zh-Hant": "直接面向消費者的品牌進入大眾零售，可能需要具備認證的產能和規模化補貨能力。",
  },
  carawaySource: {
    en: "Caraway announcement",
    "zh-Hans": "Caraway 公告",
    "zh-Hant": "Caraway 公告",
  },
  carawayDate: {
    en: "13 Jul 2026",
    "zh-Hans": "2026 年 7 月 13 日",
    "zh-Hant": "2026 年 7 月 13 日",
  },
  toys: {
    en: "Toys / STEM",
    "zh-Hans": "玩具 / STEM 教育",
    "zh-Hant": "玩具 / STEM 教育",
  },
  channelExpansion: {
    en: "Channel expansion",
    "zh-Hans": "渠道扩张",
    "zh-Hant": "渠道擴張",
  },
  toysHappened: {
    en: "Six Barbie STEAM kits launched at Walmart before expanding to additional channels.",
    "zh-Hans": "六款 Barbie STEAM 套装在 Walmart 上市，随后将拓展至其他渠道。",
    "zh-Hant": "六款 Barbie STEAM 套裝在 Walmart 上市，隨後將拓展至其他渠道。",
  },
  toysMatters: {
    en: "Channel expansion can reopen assortment, distribution and production requirements.",
    "zh-Hans": "渠道扩张可能带来新的选品、分销及生产需求。",
    "zh-Hant": "渠道擴張可能帶來新的選品、分銷及生產需求。",
  },
  toysDate: {
    en: "5 Aug 2026",
    "zh-Hans": "2026 年 8 月 5 日",
    "zh-Hant": "2026 年 8 月 5 日",
  },
  medical: {
    en: "Medical / elder care",
    "zh-Hans": "医疗 / 养老",
    "zh-Hant": "醫療 / 安老",
  },
  marketEntry: {
    en: "Market entry",
    "zh-Hans": "进入新市场",
    "zh-Hant": "進入新市場",
  },
  nekoHappened: {
    en: "Raised a $700M Series C ahead of the opening of its first US clinics.",
    "zh-Hans": "在首批美国诊所开业前，完成 7 亿美元 C 轮融资。",
    "zh-Hant": "在首批美國診所開業前，完成 7 億美元 C 輪融資。",
  },
  nekoMatters: {
    en: "A US clinic rollout may create equipment, fit-out and vendor-qualification needs.",
    "zh-Hans": "在美国开设诊所，可能带来设备、装修及供应商资质审核需求。",
    "zh-Hant": "在美國開設診所，可能帶來設備、裝修及供應商資質審核需求。",
  },
  nekoDate: {
    en: "15 Jul 2026",
    "zh-Hans": "2026 年 7 月 15 日",
    "zh-Hant": "2026 年 7 月 15 日",
  },
  packaging: {
    en: "Foodservice packaging",
    "zh-Hans": "餐饮包装",
    "zh-Hant": "餐飲包裝",
  },
  ecoHappened: {
    en: "Launched its reusable, recyclable and compostable product range in the UK.",
    "zh-Hans": "在英国推出可重复使用、可回收及可堆肥的产品系列。",
    "zh-Hant": "在英國推出可重複使用、可回收及可堆肥的產品系列。",
  },
  ecoMatters: {
    en: "A new-market rollout can create local assortment and supply requirements.",
    "zh-Hans": "进入新市场可能带来本地选品与供应需求。",
    "zh-Hant": "進入新市場可能帶來本地選品與供應需求。",
  },
  ecoDate: {
    en: "2 Feb 2026",
    "zh-Hans": "2026 年 2 月 2 日",
    "zh-Hant": "2026 年 2 月 2 日",
  },
  seoTitle: {
    en: "Buyer Signal Radar for Hong Kong Exporters | Mindstream",
    "zh-Hans": "买家商机雷达 · 为香港出口商发现采购时机 | Mindstream",
    "zh-Hant": "買家商機雷達 · 為香港出口商發現採購時機 | Mindstream",
  },
  seoDescription: {
    en: "Get 3–6 evidence-backed overseas buyer signals each week, with source links, timing context and who to approach. Apply for a Mindstream pilot.",
    "zh-Hans": "每周获取 3–6 条有来源支持的海外买家信号，了解采购时机、原始证据及该联系谁。申请 Mindstream 首批试用。",
    "zh-Hant": "每週獲取 3–6 條有來源支持的海外買家訊號，了解採購時機、原始證據及該聯絡誰。申請 Mindstream 首批試用。",
  },
  seoImageAlt: {
    en: "Mindstream Buyer Signal Radar for Hong Kong exporters",
    "zh-Hans": "Mindstream 面向香港出口商的买家商机雷达",
    "zh-Hant": "Mindstream 面向香港出口商的買家商機雷達",
  },
} satisfies Record<string, Record<RadarLocale, string>>;

export function radarLandingCopy(locale: RadarLocale) {
  return Object.fromEntries(Object.entries(messages).map(([key, value]) => [key, value[locale]])) as Record<keyof typeof messages, string>;
}
